import React, { useState, useEffect } from 'react';
import { AuditLog, AuditLogFilters } from '../../types';
import { auditApi } from '../../services/api';
import { format } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import TableSkeleton from '../common/TableSkeleton';
import AuditFiltersSkeleton from '../common/AuditFiltersSkeleton';
import { convertToCSV, downloadCSV, formatDateForCSV, formatObjectForCSV, CSVColumn } from '../../utils/csvExport';

interface AuditLogListProps {
  filters?: AuditLogFilters;
  onFilterChange?: (filters: AuditLogFilters) => void;
}

const AuditLogList: React.FC<AuditLogListProps> = ({ filters, onFilterChange }) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await auditApi.getAuditLogs({
        ...filters,
        skip: ((currentPage - 1) * 100),
        limit: 100
      });
      console.log('Audit logs response:', response.data);
      if (response.data.data) {
        setLogs(response.data.data);
        setTotalPages(response.data.meta.pages);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [filters, currentPage]);

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-primary-500/10 text-primary-500 dark:text-primary-400';
      case 'update':
        return 'bg-blue-500/10 text-blue-500 dark:text-blue-400';
      case 'delete':
        return 'bg-red-500/10 text-red-500 dark:text-red-400';
      default:
        return 'bg-gray-500/10 text-gray-500 dark:text-gray-400';
    }
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value.toString();
  };

  const handleDownloadCSV = async () => {
    try {
      setIsDownloading(true);
      
      // Fetch all audit logs with current filters
      const response = await auditApi.getAllAuditLogs(filters);
      const allLogs = response.data.data || [];

      // Define CSV columns
      const csvColumns: CSVColumn[] = [
        { key: 'created_at', label: 'Time', formatter: formatDateForCSV },
        { key: 'action_type', label: 'Action' },
        { key: 'entity_type', label: 'Entity Type' },
        { key: 'entity_id', label: 'Entity ID' },
        { key: 'entity_display', label: 'Entity Display' },
        { key: 'action_by_email', label: 'Action By Email' },
        { key: 'action_by_name', label: 'Action By Name' },
        { key: 'ip_address', label: 'IP Address' },
        { key: 'old_values', label: 'Old Values', formatter: formatObjectForCSV },
        { key: 'new_values', label: 'New Values', formatter: formatObjectForCSV },
      ];

      // Convert to CSV
      const csvContent = convertToCSV(allLogs, csvColumns);
      
      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      const filename = `audit-logs-${dateStr}.csv`;
      
      // Download the file
      downloadCSV(csvContent, filename);
      
    } catch (error) {
      console.error('Failed to download audit logs:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading && !logs.length) {
    return (
      <div className="flex flex-col h-full space-y-6">
        <AuditFiltersSkeleton />
        <div className="overflow-hidden">
          <TableSkeleton rows={10} columns={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-6">
      {/* Filters and Download */}
      <div className="p-6 bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Audit Logs</h3>
          <button
            onClick={handleDownloadCSV}
            disabled={isDownloading}
            className="px-4 py-2 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-white transition-colors duration-200 flex items-center gap-2"
          >
            <ArrowDownTrayIcon className={`w-4 h-4 ${isDownloading ? 'animate-spin' : ''}`} />
            {isDownloading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Entity Type</label>
          <select
            className="mt-1 block w-full rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
            value={filters?.entity_type || ''}
            onChange={(e) => onFilterChange?.({ ...filters, entity_type: e.target.value })}
          >
            <option value="">All</option>
            <option value="users">Users</option>
            <option value="organizations">Organizations</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Action Type</label>
          <select
            className="mt-1 block w-full rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
            value={filters?.action_type || ''}
            onChange={(e) => onFilterChange?.({ ...filters, action_type: e.target.value })}
          >
            <option value="">All</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Range</label>
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              className="mt-1 block w-full rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
              value={filters?.start_date?.split('T')[0] || ''}
              onChange={(e) => onFilterChange?.({ ...filters, start_date: e.target.value ? `${e.target.value}T00:00:00Z` : undefined })}
            />
            <input
              type="date"
              className="mt-1 block w-full rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200"
              value={filters?.end_date?.split('T')[0] || ''}
              onChange={(e) => onFilterChange?.({ ...filters, end_date: e.target.value ? `${e.target.value}T23:59:59Z` : undefined })}
            />
          </div>
        </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="overflow-hidden">
        <div className="w-full align-middle">
          <div className="relative">
            <div className="overflow-y-auto overflow-x-auto" style={{ maxHeight: 'calc(100vh - 450px)' }}>
              {loading ? (
                <TableSkeleton rows={10} columns={5} />
              ) : (
                <table className="min-w-full divide-y divide-light-border dark:divide-dark-border">
                  <thead className="bg-light-card dark:bg-dark-card">
                    <tr>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                        Time
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                        Action
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                        Entity
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                        Changes
                      </th>
                      <th className="sticky top-0 px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-light-surface dark:bg-dark-surface">
                        IP Address
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-light-card dark:bg-dark-card divide-y divide-light-border dark:divide-dark-border">
                    {logs.map((log) => (
                      <tr key={log.log_id} className="hover:bg-light-hover dark:hover:bg-dark-hover transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs font-medium rounded-full ${getActionColor(log.action_type)}`}>
                            {log.action_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {log.entity_display || `${log.entity_type} #${log.entity_id}`}
                            </div>
                            {log.action_by_email && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Done by: {log.action_by_email}
                                {log.action_by_name && ` (${log.action_by_name})`}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          <pre className="whitespace-pre-wrap">
                            {log.action_type === 'delete' 
                              ? 'Deleted'
                              : Object.entries(log.new_values || {}).map(([key, value]) => (
                                  <div key={key}>
                                    <strong>{key}:</strong> {formatValue(log.old_values?.[key])} → {formatValue(value)}
                                  </div>
                                ))
                            }
                          </pre>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.ip_address}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 flex items-center justify-between border-t border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Page <span className="font-medium text-gray-900 dark:text-white">{currentPage}</span> of{' '}
            <span className="font-medium text-gray-900 dark:text-white">{totalPages}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentPage === 1
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
            }`}
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              currentPage === totalPages
                ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-600 dark:text-gray-400 hover:bg-light-hover dark:hover:bg-dark-hover'
            }`}
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogList;
